/**
 * OllamaLabHubModal — Cognify AI Lab
 * Hub modal for the "Ollama (local LLMs)" section on the OPO page.
 *
 * Tabs: Registry | Scrape Runs
 * Registry drills into a per-model detail view (metadata, eval history across
 * test phases, run list, community sentiment scaffold, scrape sources editor).
 *
 * API: /api/opo/ai-lab (server-api/opo-ai-lab.js)
 * Docs: Docs/02-SERVICES/OPO_SERVICE/COGNIFY_AI_LAB.md
 */
import React, { useState, useEffect, useCallback } from 'react';
import './OllamaLabHubModal.css';

const API = '/api/opo/ai-lab';

const TABS = [
  { id: 'registry', label: 'Registry' },
  { id: 'runs', label: 'Scrape Runs' },
];

function fmtBytes(n) {
  if (n == null) return '—';
  const gb = Number(n) / 1e9;
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${Math.round(Number(n) / 1e6)} MB`;
}
function fmtPct(v) {
  if (v == null) return '—';
  const n = Number(v);
  // accuracy metrics are 0..1; score_pct already 0..100
  return n <= 1 ? `${(n * 100).toFixed(1)}%` : `${n.toFixed(1)}%`;
}
function fmtMs(v) {
  if (v == null) return '—';
  const n = Number(v);
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}
function fmtDate(s) {
  return s ? new Date(s).toLocaleDateString('en-CA') : '—';
}
function fmtDateTime(s) {
  return s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

function scoreClass(v) {
  if (v == null) return '';
  const pct = Number(v) <= 1 ? Number(v) * 100 : Number(v);
  if (pct >= 80) return 'lab-score-good';
  if (pct >= 50) return 'lab-score-mid';
  return 'lab-score-low';
}

// ── Registry grid ────────────────────────────────────────────────────────────
function RegistryGrid({ models, onSelect }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | tested | untested | uninstalled
  const [familyFilter, setFamilyFilter] = useState('all');

  const families = [...new Set(models.map((m) => m.family).filter(Boolean))].sort();

  const filtered = models.filter((m) => {
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'tested' && !m.tested) return false;
    if (filter === 'untested' && (m.tested || !m.installed)) return false;
    if (filter === 'uninstalled' && m.installed) return false;
    if (familyFilter !== 'all' && m.family !== familyFilter) return false;
    return true;
  });

  return (
    <div>
      <div className="lab-toolbar">
        <input
          className="lab-search"
          type="text"
          placeholder="Search models…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="lab-filter-pills">
          {['all', 'tested', 'untested', 'uninstalled'].map((f) => (
            <button
              key={f}
              type="button"
              className={`lab-pill ${filter === f ? 'lab-pill-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <select className="lab-family-select" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}>
          <option value="all">All families</option>
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      <div className="lab-grid">
        {filtered.map((m) => (
          <button key={m.name} type="button" className="lab-model-card" onClick={() => onSelect(m.name)}>
            <div className="lab-card-top">
              <span className="lab-model-name">{m.name}</span>
              {m.tested
                ? <span className="lab-badge lab-badge-tested">TESTED</span>
                : <span className="lab-badge lab-badge-untested">UNTESTED</span>}
            </div>
            <div className="lab-card-meta">
              <span>{m.param_size || '—'}</span>
              <span>{m.quantization || '—'}</span>
              <span>{fmtBytes(m.size_bytes)}</span>
              {!m.installed && <span className="lab-badge lab-badge-gone">NOT INSTALLED</span>}
            </div>
            {m.tested && (
              <div className="lab-card-stats">
                {m.best_accuracy != null && (
                  <span className={scoreClass(m.best_accuracy)}>Best acc {fmtPct(m.best_accuracy)}</span>
                )}
                {m.avg_score_pct != null && (
                  <span className={scoreClass(m.avg_score_pct)}>Avg score {fmtPct(m.avg_score_pct)}</span>
                )}
                <span className="lab-muted">
                  {(m.eval_responses || 0) + (m.focused_runs || 0) + (m.beta_runs || 0)} runs
                </span>
              </div>
            )}
            {m.sentiment && m.sentiment.community_score != null && (
              <div className="lab-card-sentiment">Community {m.sentiment.community_score}/10</div>
            )}
          </button>
        ))}
        {filtered.length === 0 && <div className="lab-empty">No models match the current filters.</div>}
      </div>
    </div>
  );
}

// ── Model detail ─────────────────────────────────────────────────────────────
function ModelDetail({ name, onBack }) {
  const [detail, setDetail] = useState(null);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceType, setNewSourceType] = useState('community');
  const [savingSource, setSavingSource] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/models/${encodeURIComponent(name)}`)
      .then((r) => r.json())
      .then((d) => (d.error ? setError(d.error) : setDetail(d)))
      .catch((e) => setError(e.message));
    fetch(`${API}/models/${encodeURIComponent(name)}/runs?limit=25`)
      .then((r) => r.json())
      .then((d) => setRuns(d.runs || []))
      .catch(() => setRuns([]));
  }, [name]);

  useEffect(() => { load(); }, [load]);

  const addSource = async () => {
    if (!newSourceUrl.trim()) return;
    setSavingSource(true);
    try {
      const res = await fetch(`${API}/models/${encodeURIComponent(name)}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newSourceUrl.trim(), source_type: newSourceType }),
      });
      const d = await res.json();
      if (!d.error) { setNewSourceUrl(''); load(); }
    } finally {
      setSavingSource(false);
    }
  };

  const removeSource = async (id) => {
    await fetch(`${API}/sources/${id}`, { method: 'DELETE' });
    load();
  };

  if (error) return (<div><button type="button" className="lab-back-btn" onClick={onBack}>← Back</button><div className="lab-error">Error: {error}</div></div>);
  if (!detail) return (<div><button type="button" className="lab-back-btn" onClick={onBack}>← Back</button><div className="lab-muted" style={{ padding: 20 }}>Loading {name}…</div></div>);

  const ev = detail.eval || {};

  return (
    <div>
      <button type="button" className="lab-back-btn" onClick={onBack}>← Back to Registry</button>

      <div className="lab-detail-header">
        <h3 className="lab-detail-name">{detail.name}</h3>
        {ev.tested
          ? <span className="lab-badge lab-badge-tested">TESTED</span>
          : <span className="lab-badge lab-badge-untested">UNTESTED</span>}
        {!detail.installed && <span className="lab-badge lab-badge-gone">NOT INSTALLED</span>}
      </div>

      <div className="lab-stat-grid">
        <div className="lab-stat-card"><div className="lab-stat-label">Family</div><div className="lab-stat-value-sm">{detail.family || '—'}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Parameters</div><div className="lab-stat-value-sm">{detail.param_size || '—'}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Quantization</div><div className="lab-stat-value-sm">{detail.quantization || '—'}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Size</div><div className="lab-stat-value-sm">{fmtBytes(detail.size_bytes)}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Best accuracy</div><div className={`lab-stat-value-sm ${scoreClass(ev.best_accuracy)}`}>{fmtPct(ev.best_accuracy)}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Avg latency</div><div className="lab-stat-value-sm">{fmtMs(ev.avg_latency_ms)}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Total runs</div><div className="lab-stat-value-sm">{(ev.eval_responses || 0) + (ev.focused_runs || 0) + (ev.beta_runs || 0)}</div></div>
        <div className="lab-stat-card"><div className="lab-stat-label">Last tested</div><div className="lab-stat-value-sm">{fmtDate(ev.last_tested_at)}</div></div>
      </div>

      {/* Test phase timeline */}
      <div className="lab-panel">
        <h4 className="lab-panel-title">Test phases</h4>
        {(detail.phases || []).length === 0 && (
          <div className="lab-muted">This model has not appeared in any evaluation phase yet.</div>
        )}
        {(detail.phases || []).length > 0 && (
          <table className="lab-table">
            <thead>
              <tr><th>Phase</th><th>As</th><th>Runs</th><th>Score</th><th>Latency</th><th>Last run</th></tr>
            </thead>
            <tbody>
              {detail.phases.map((p, i) => (
                <tr key={i}>
                  <td>{p.phase}</td>
                  <td className="lab-mono">{p.identifier}</td>
                  <td>{p.runs}</td>
                  <td className={scoreClass(p.avg_score_pct ?? p.best_accuracy)}>
                    {p.avg_score_pct != null ? fmtPct(p.avg_score_pct) : fmtPct(p.best_accuracy)}
                  </td>
                  <td>{fmtMs(p.avg_latency_ms)}</td>
                  <td>{fmtDate(p.last_run)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent runs */}
      <div className="lab-panel">
        <h4 className="lab-panel-title">Recent runs</h4>
        {runs === null && <div className="lab-muted">Loading runs…</div>}
        {runs !== null && runs.length === 0 && <div className="lab-muted">No individual runs recorded.</div>}
        {runs !== null && runs.length > 0 && (
          <table className="lab-table">
            <thead>
              <tr><th>Source</th><th>Question</th><th>Score</th><th>Latency</th><th>When</th></tr>
            </thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={i}>
                  <td>{r.source}{r.track ? ` · ${r.track}` : ''}</td>
                  <td className="lab-question">{r.question || '—'}</td>
                  <td className={scoreClass(r.score)}>{r.score != null ? fmtPct(r.score) : '—'}</td>
                  <td>{fmtMs(r.latency_ms)}</td>
                  <td>{fmtDateTime(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Community sentiment (phase-2 scaffold) */}
      <div className="lab-panel">
        <h4 className="lab-panel-title">Community sentiment</h4>
        {detail.sentiment && detail.sentiment.summary_md ? (
          <div>
            <div className="lab-sentiment-score">
              Community score: <strong>{detail.sentiment.community_score ?? '—'}/10</strong>
              <span className="lab-muted"> · {detail.sentiment.sources_count || 0} sources · {fmtDate(detail.sentiment.generated_at)}</span>
            </div>
            <pre className="lab-sentiment-md">{detail.sentiment.summary_md}</pre>
          </div>
        ) : (
          <div className="lab-muted">
            No sentiment digest yet. The daily model-scout will scrape the sources below
            (official docs + community reviews) and generate an assessment here.
          </div>
        )}
        {(detail.recent_articles || []).length > 0 && (
          <ul className="lab-article-list">
            {detail.recent_articles.map((a) => (
              <li key={a.id}>
                <a href={a.url} target="_blank" rel="noreferrer">{a.title || a.url}</a>
                <span className="lab-muted"> — {fmtDate(a.fetched_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Scrape sources editor */}
      <div className="lab-panel">
        <h4 className="lab-panel-title">Scrape sources</h4>
        <div className="lab-source-add">
          <select value={newSourceType} onChange={(e) => setNewSourceType(e.target.value)} className="lab-family-select">
            <option value="official">Official</option>
            <option value="community">Community</option>
            <option value="benchmark">Benchmark</option>
          </select>
          <input
            className="lab-search"
            type="text"
            placeholder="https://… source URL for this model"
            value={newSourceUrl}
            onChange={(e) => setNewSourceUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSource()}
          />
          <button type="button" className="lab-btn" onClick={addSource} disabled={savingSource || !newSourceUrl.trim()}>
            {savingSource ? 'Adding…' : 'Add source'}
          </button>
        </div>
        {(detail.sources || []).length === 0 && (
          <div className="lab-muted">No sources yet. Add official model pages, Reddit/HN threads, benchmark pages.</div>
        )}
        {(detail.sources || []).length > 0 && (
          <table className="lab-table">
            <thead><tr><th>Type</th><th>URL</th><th>Last fetched</th><th></th></tr></thead>
            <tbody>
              {detail.sources.map((s) => (
                <tr key={s.id}>
                  <td><span className={`lab-badge lab-badge-${s.source_type}`}>{s.source_type}</span></td>
                  <td className="lab-question"><a href={s.url} target="_blank" rel="noreferrer">{s.url}</a></td>
                  <td>{fmtDate(s.last_fetched_at)}</td>
                  <td><button type="button" className="lab-btn-del" onClick={() => removeSource(s.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Scrape runs tab ──────────────────────────────────────────────────────────
function ScrapeRuns({ onTrigger }) {
  const [runs, setRuns] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/scrape/runs`)
      .then((r) => r.json())
      .then((d) => setRuns(d.runs || []))
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const trigger = async () => {
    setTriggering(true);
    setMsg(null);
    try {
      const res = await fetch(`${API}/scrape/run`, { method: 'POST' });
      const d = await res.json();
      setMsg(d.already_running ? 'A run is already in progress.' : `Run #${d.run_id} started.`);
      load();
      if (onTrigger) onTrigger();
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div>
      <div className="lab-panel">
        <h4 className="lab-panel-title">Model scout</h4>
        <p className="lab-muted">
          The daily model-scout syncs the Ollama registry and (phase 2) scrapes official +
          community sources per model into sentiment digests. Runs daily via PM2
          (<span className="lab-mono">dev-cognify-lab-daily</span>) — or trigger manually.
        </p>
        <button type="button" className="lab-btn" onClick={trigger} disabled={triggering}>
          {triggering ? 'Starting…' : 'Run scout now'}
        </button>
        {msg && <span className="lab-muted" style={{ marginLeft: 10 }}>{msg}</span>}
      </div>
      <div className="lab-panel">
        <h4 className="lab-panel-title">Recent runs</h4>
        {runs === null && <div className="lab-muted">Loading…</div>}
        {runs !== null && runs.length === 0 && <div className="lab-muted">No scrape runs yet.</div>}
        {runs !== null && runs.length > 0 && (
          <table className="lab-table">
            <thead><tr><th>#</th><th>Status</th><th>Started</th><th>Finished</th><th>Stats</th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    <span className={`lab-badge ${r.status === 'completed' ? 'lab-badge-tested' : r.status === 'failed' ? 'lab-badge-failed' : 'lab-badge-running'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{fmtDateTime(r.started_at)}</td>
                  <td>{fmtDateTime(r.finished_at)}</td>
                  <td className="lab-mono">{r.stats ? JSON.stringify(r.stats) : '—'}{r.error_message ? ` · ${r.error_message}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export default function OllamaLabHubModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('registry');
  const [overview, setOverview] = useState(null);
  const [models, setModels] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [scanning, setScanning] = useState(false);

  const loadAll = useCallback(() => {
    fetch(`${API}/overview`).then((r) => r.json()).then(setOverview).catch(() => {});
    fetch(`${API}/models`).then((r) => r.json()).then((d) => setModels(d.models || [])).catch(() => setModels([]));
  }, []);

  useEffect(() => {
    if (isOpen) loadAll();
  }, [isOpen, loadAll]);

  if (!isOpen) return null;

  const rescan = async () => {
    setScanning(true);
    try {
      await fetch(`${API}/scan`, { method: 'POST' });
      loadAll();
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="lab-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lab-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lab-header">
          <div className="lab-header-left">
            <span className="lab-header-icon">🧪</span>
            <h2 className="lab-header-title">Cognify AI Lab</h2>
            <span className="lab-header-dept">Ollama local LLMs — Cognify Business Automation Inc.</span>
          </div>
          <div className="lab-header-right">
            {overview && (
              <div className="lab-header-chips">
                <span className="lab-chip">{overview.installed} installed</span>
                <span className="lab-chip lab-chip-green">{overview.tested} tested</span>
                <span className="lab-chip lab-chip-amber">{overview.untested} untested</span>
              </div>
            )}
            <button type="button" className="lab-btn" onClick={rescan} disabled={scanning}>
              {scanning ? 'Scanning…' : '↻ Rescan'}
            </button>
            <button type="button" className="lab-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="lab-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`lab-tab ${activeTab === t.id ? 'lab-tab-active' : ''}`}
              onClick={() => { setActiveTab(t.id); setSelectedModel(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="lab-content">
          {activeTab === 'registry' && !selectedModel && (
            models === null
              ? <div className="lab-muted" style={{ padding: 20 }}>Loading models…</div>
              : <RegistryGrid models={models} onSelect={setSelectedModel} />
          )}
          {activeTab === 'registry' && selectedModel && (
            <ModelDetail name={selectedModel} onBack={() => setSelectedModel(null)} />
          )}
          {activeTab === 'runs' && <ScrapeRuns onTrigger={loadAll} />}
        </div>
      </div>
    </div>
  );
}
